/**
 * FeaturedProductSlider — three featured products sharing ONE set of display
 * options (CM Enhanced Content design, Figma `CM-Enhanced-Content` node
 * 93:1381). Authors list products in priority order; the first three in stock
 * are shown and the rest stand by as backups.
 *
 * Desktop (lg, 984px+): three across. Tablet + large phones (sm, 600px+): a
 * swipe row, two visible + a peek of the third. Mobile (<600px): one at 85%
 * (min 270px) + a peek of the next. Native scroll-snap — no carousel JS to
 * hydrate for three slides.
 *
 * Products load client-side in ONE batched read. While it is in flight the
 * row holds card-shaped skeletons; if every product turns out unavailable the
 * section collapses (an empty headline + logo is worse than nothing).
 */
import { useIntl } from "react-intl";

import { SLIDER_TILE_DISPLAY } from "~/hec/components/organisms/dynamic-product-tile/display-defaults";
import { DynamicProductTile } from "~/hec/components/organisms/dynamic-product-tile/dynamic-product-tile";
import { FeatureProductCardSkeleton } from "~/hec/components/organisms/dynamic-product-tile/feature-product-card-skeleton";
import { ProductTileSkeleton } from "~/hec/components/product/product-tile-skeleton";
import {
  duplicateProductIds,
  firstRowByProductId,
} from "~/hec/dynamic-product-tile/dynamic-product-tile.logic";
import { useFeaturedProducts } from "~/hec/dynamic-product-tile/use-featured-products";
import { cn } from "~/lib/utils";

import messages from "./messages";
import type { FeaturedProductSliderProps } from "./types";

const VISIBLE_COUNT = 3;

/**
 * GA4 `item_list_id` for every slider tile. Stable on purpose — the headline
 * is editable copy, and a list id that changes with it splits the report.
 * The human-readable name (`item_list_name`) is the headline.
 */
const LIST_ID = "featured-product-slider";

// Mobile (<sm, Figma): 18px gap, slides top-aligned. sm+: gap-6 = 24px.
// Tablet: 2 full slides + 25% of a third → 2.25 slides and two gaps.
// Desktop: tiles cap at the design's 403px (3 × 403 + 2 × 24 = the 1257px
// track), centred when fewer than three are in stock.
const SLIDE_CLASS =
  "shrink-0 snap-start basis-[85%] sm:basis-[calc((100%_-_48px)/2.25)] lg:basis-0 lg:flex-1 lg:max-w-[403px] min-w-[270px]";

export function FeaturedProductSlider({
  items,
  tileOptions,
  headline,
  background,
  logoSrc,
  logoAlt = "",
  logoHref,
  listName,
  inEditor = false,
  className,
  style,
  ...rest
}: FeaturedProductSliderProps) {
  const intl = useIntl();
  const { products, skipped, loading } = useFeaturedProducts(
    items.map(item => item.productId),
    VISIBLE_COUNT,
  );
  const filledCount = items.filter(item => item.productId).length;
  const hasIds = filledCount > 0;
  if (!inEditor && !loading && products.length === 0) return null;

  const logo = logoSrc ? (
    <img src={logoSrc} alt={logoAlt} className="h-9 w-auto" loading="lazy" />
  ) : null;
  const gtmListName = listName || headline || LIST_ID;
  const itemById = firstRowByProductId(items);
  const duplicates = inEditor
    ? duplicateProductIds(items.map(item => item.productId))
    : [];
  const skeletons = loading ? Math.min(VISIBLE_COUNT, filledCount) : 0;
  const featureLayout =
    (tileOptions.layout ?? SLIDER_TILE_DISPLAY.layout) === "feature";

  return (
    <section
      className={cn(
        "flex flex-col items-center gap-6 py-12 sm:gap-12",
        className,
      )}
      style={{ ...(background?.trim() && { background }), ...style }}
      data-testid="featured-product-slider-section"
      {...rest}
    >
      {headline && (
        <h2 className="px-4 text-center font-grotesque text-[24px] font-medium leading-[1.2] text-blue-04 lg:text-[30px]">
          {headline}
        </h2>
      )}

      {inEditor && !hasIds ? (
        <div
          className="mx-4 flex min-h-[240px] w-full max-w-[1257px] items-center justify-center border-2 border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500"
          data-testid="featured-product-slider-empty"
        >
          Add products in priority order — the first 3 in stock are shown, the
          rest are backups.
        </div>
      ) : (
        <div
          className="no-scrollbar flex w-full max-w-[1257px] snap-x snap-mandatory scroll-px-4 items-start gap-[18px] overflow-x-auto px-4 sm:items-stretch sm:gap-[24px] lg:justify-center lg:overflow-visible lg:px-[24px]"
          data-testid="featured-product-slider-row"
        >
          {products.map(({ id, product }, i) => {
            const item = itemById.get(id);
            return (
              <div key={id} className={SLIDE_CLASS}>
                <DynamicProductTile
                  productId={id}
                  product={product}
                  greatFor={item?.greatFor}
                  keyBenefits={item?.keyBenefits}
                  imageSrc={item?.imageSrc}
                  {...tileOptions}
                  listId={LIST_ID}
                  listName={gtmListName}
                  listIndex={i + 1}
                  inEditor={inEditor}
                  className="h-full"
                />
              </div>
            );
          })}
          {Array.from({ length: skeletons }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              className={SLIDE_CLASS}
              data-testid="featured-product-slider-loading"
            >
              {featureLayout ? (
                <FeatureProductCardSkeleton />
              ) : (
                <div className="bg-white">
                  <ProductTileSkeleton />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {duplicates.length > 0 && (
        <p
          className="px-4 text-center text-sm text-gray-500"
          data-testid="featured-product-slider-duplicates-message"
        >
          Repeated SKU — only the first row is used: {duplicates.join(", ")}
        </p>
      )}

      {inEditor && skipped.length > 0 && (
        <p
          className="px-4 text-center text-sm text-gray-500"
          data-testid="featured-product-slider-skipped-message"
        >
          Using backups — skipped (out of stock / not found):{" "}
          {skipped.join(", ")}
        </p>
      )}

      {logoSrc &&
        (logoHref?.trim() ? (
          // Plain <a>: the target may be a Workers route, an SFRA page or
          // off-site — a full navigation is right for all three.
          <a
            href={logoHref.trim()}
            // A linked logo with no alt text would be a nameless link.
            aria-label={
              logoAlt.trim()
                ? undefined
                : intl.formatMessage(messages.logoLinkFallback)
            }
            data-testid="featured-product-slider-logo-link"
          >
            {logo}
          </a>
        ) : (
          logo
        ))}
    </section>
  );
}
